import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import type { FC } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ISRAEL_CBS_BOUNDARY_ATTRIBUTION } from "@shared/helpers/israel-cbs-regions";
import type { StatisticsGeographicRegionRow } from "../types";

type ComparisonLevel = "district" | "sub_district";

interface StatisticsGeographicComparisonChartProps {
  districtComparison: StatisticsGeographicRegionRow[];
  subDistrictComparison: StatisticsGeographicRegionRow[];
  level: ComparisonLevel;
  onLevelChange: (level: ComparisonLevel) => void;
  isLoading?: boolean;
}

export const StatisticsGeographicComparisonChart: FC<
  StatisticsGeographicComparisonChartProps
> = ({
  districtComparison,
  subDistrictComparison,
  level,
  onLevelChange,
  isLoading,
}) => {
  const regions =
    level === "district" ? districtComparison : subDistrictComparison;

  const chartData = regions.map((r) => ({
    regionLabel: r.regionLabel,
    מתנדבים: r.volunteerCount,
    "משימות פתוחות": r.openOpportunityCount,
    demandGap: r.demandGap,
  }));

  const maxValue = chartData.length
    ? Math.max(
        ...chartData.map((r) => Math.max(r["מתנדבים"], r["משימות פתוחות"])),
      )
    : 0;
  const yAxisWidth = maxValue >= 1000 ? 56 : maxValue >= 100 ? 48 : 40;
  const chartHeight = level === "district" ? 320 : 420;
  const xTickFontSize = level === "district" ? 12 : 10;

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", minWidth: 0, py: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          טוען…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "start" } }}>
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            מתנדבים מול משימות פתוחות לפי אזור
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            שיוך לפי מיקום GPS (point-in-polygon). פער = משימות פתוחות − מתנדבים.
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={level}
          onChange={(_, v: ComparisonLevel | null) => v && onLevelChange(v)}
          sx={{
            alignSelf: { sm: "center" },
            bgcolor: "background.paper",
            borderRadius: 999,
            p: 0.35,
            border: "1px solid",
            borderColor: "divider",
            "& .MuiToggleButton-root": {
              border: 0,
              borderRadius: 999,
              px: 1.5,
              py: 0.55,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8125rem",
              "&.Mui-selected": {
                bgcolor: "#05204c",
                color: "#fff",
                "&:hover": { bgcolor: "#0a3a8a" },
              },
            },
          }}
        >
          <ToggleButton value="district">6 מחוזות</ToggleButton>
          <ToggleButton value="sub_district">15 נפות</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!chartData.length ? (
        <Box
          sx={{
            py: 2,
            px: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            אין נתונים — ודאו שהרצתם seed לגבולות CBS (ראו server/scripts/seed-israel-cbs-boundaries.ts)
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 12,
                right: 8,
                left: 8,
                bottom: level === "sub_district" ? 28 : 8,
              }}
            >
              <CartesianGrid strokeDasharray="4 4" />
              <XAxis
                dataKey="regionLabel"
                tick={{ fontSize: xTickFontSize }}
                interval={0}
                angle={level === "sub_district" ? -35 : 0}
                textAnchor={level === "sub_district" ? "end" : "middle"}
                height={level === "sub_district" ? 72 : 30}
              />
              <YAxis
                allowDecimals={false}
                width={yAxisWidth}
                tickMargin={6}
                tick={{ fontSize: 11 }}
              />
              <RechartsTooltip
                contentStyle={{ direction: "rtl" }}
                formatter={(value: number, name: string) => [
                  value.toLocaleString("he-IL"),
                  name,
                ]}
                labelFormatter={(label) => `אזור: ${label}`}
              />
              <Legend />
              <Bar
                dataKey="מתנדבים"
                fill="#065bf9"
                radius={[6, 6, 0, 0]}
                maxBarSize={level === "district" ? 52 : 36}
              />
              <Bar
                dataKey="משימות פתוחות"
                fill="#e65100"
                radius={[6, 6, 0, 0]}
                maxBarSize={level === "district" ? 52 : 36}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        {ISRAEL_CBS_BOUNDARY_ATTRIBUTION}
      </Typography>
    </Box>
  );
};
